import React, { useMemo } from "react";
import type { MenuProps, OptionProps } from "react-select";
import ReactSelect, { components } from "react-select";

import { useCurrencyStore } from "@calcom/lib/currencyStore";

const CURRENCY_META: Record<string, { name: string }> = {
  USD: { name: "US Dollar" },
  GBP: { name: "British Pound" },
  EUR: { name: "Euro" },
  AUD: { name: "Australian Dollar" },
  CAD: { name: "Canadian Dollar" },
  NZD: { name: "New Zealand Dollar" },
  JPY: { name: "Japanese Yen" },
  CHF: { name: "Swiss Franc" },
  SEK: { name: "Swedish Krona" },
  NOK: { name: "Norwegian Krone" },
  DKK: { name: "Danish Krone" },
  INR: { name: "Indian Rupee" },
  SGD: { name: "Singapore Dollar" },
  HKD: { name: "Hong Kong Dollar" },
  ZAR: { name: "South African Rand" },
  AED: { name: "UAE Dirham" },
  BGN: { name: "BGN" },
  BRL: { name: "Brazilian Real" },
  CNY: { name: "Chinese Yuan" },
  CZK: { name: "Czech Koruna" },
  HUF: { name: "Hungarian Forint" },
  IDR: { name: "Indonesian Rupiah" },
  ILS: { name: "Israeli New Shekel" },
  ISK: { name: "Icelandic Króna" },
  KRW: { name: "South Korean Won" },
  MXN: { name: "Mexican Peso" },
  MYR: { name: "Malaysian Ringgit" },
  PHP: { name: "Philippine Piso" },
  PLN: { name: "Polish Złoty" },
  RON: { name: "Romanian Leu" },
  THB: { name: "Thai Baht" },
  TRY: { name: "Turkish Lira" },
};

const COMMON = ["GBP", "USD", "EUR", "AUD", "CAD", "NZD", "JPY"];
const FLAGS: Record<string, string> = {
  USD: "🇺🇸",
  GBP: "🇬🇧",
  EUR: "🇪🇺",
  AUD: "🇦🇺",
  CAD: "🇨🇦",
  NZD: "🇳🇿",
  JPY: "🇯🇵",
  CHF: "🇨🇭",
  SEK: "🇸🇪",
  NOK: "🇳🇴",
  DKK: "🇩🇰",
  INR: "🇮🇳",
  SGD: "🇸🇬",
  HKD: "🇭🇰",
  ZAR: "🇿🇦",
  AED: "🇦🇪",
  BGN: "🇧🇬",
  BRL: "🇧🇷",
  CNY: "🇨🇳",
  CZK: "🇨🇿",
  HUF: "🇭🇺",
  IDR: "🇮🇩",
  ILS: "🇮🇱",
  ISK: "🇮🇸",
  KRW: "🇰🇷",
  MXN: "🇲🇽",
  MYR: "🇲🇾",
  PHP: "🇵🇭",
  PLN: "🇵🇱",
  RON: "🇷🇴",
  THB: "🇹🇭",
  TRY: "🇹🇷",
};

// Custom menu: scrollable list + sticky footer (like your other file)
const CustomMenu = (props: MenuProps<any>) => (
  <components.Menu {...props}>
    <div className="max-h-[60vh] overflow-y-auto overscroll-contain py-2">{props.children}</div>
    <div className="border-subtle bg-popover text-subtle sticky bottom-0 border-t px-4 py-2 text-xs">
      Rates by Frankfurter (ECB reference)
    </div>
  </components.Menu>
);

// Custom option row: flag + "CODE — Name"
const CustomOption = (props: OptionProps<any>) => {
  const code = String(props.data.value).toUpperCase();
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2 px-2 py-1">
        <span className="text-sm">{FLAGS[code] ?? "🌐"}</span>
        <span className="text-sm">
          {code} — {CURRENCY_META[code]?.name ?? code}
        </span>
      </div>
    </components.Option>
  );
};

export default function CurrencySelect({
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
}: {
  menuIsOpen?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
}) {
  const currency = useCurrencyStore((s) => s.currency);
  const setCurrency = useCurrencyStore((s) => s.setCurrency);
  const rates = useCurrencyStore((s) => s.rates);

  const options = useMemo(() => {
    const codes = Object.keys(rates || {});
    const base = (codes.length ? codes : COMMON).map((c) => c.toUpperCase());
    const uniques = Array.from(new Set(base));
    const commonFirst = COMMON.filter((c) => uniques.includes(c));
    const rest = uniques.filter((c) => !COMMON.includes(c)).sort();
    return [...commonFirst, ...rest].map((code) => ({
      value: code,
      label: `${code} — ${CURRENCY_META[code]?.name ?? code}`,
    }));
  }, [rates]);

  const current = options.find((o) => o.value === (currency || "USD").toUpperCase()) || options[0];

  //  like TimezoneSelect (transparent, borderless control)
  const classNames = {
    control: () =>
      "!cursor-pointer border-default text-sm leading-4 placeholder:text-sm placeholder:font-normal " +
      "focus-within:ring-emphasis hover:border-emphasis rounded-md gap-1 !min-h-0 p-0 w-full " +
      "border-0 bg-transparent focus-within:ring-0",
    valueContainer: () => "text-emphasis placeholder:text-muted flex gap-1",
    singleValue: () => "placeholder:text-muted text-text py-1",
    input: () => "focus:ring-0 focus:ring-offset-0 !text-emphasis h-6 md:max-w-[145px] max-w-[250px]",
    indicatorsContainer: () => "text-default ml-auto",
    indicatorSeparator: () => "hidden",
    container: () => "max-w-full",
    menu: () => "!w-[300px] p-0 !bg-default !text-default !border !border-subtle !rounded-md !shadow-lg",
    menuList: () => "!bg-default p-0",
    option: (state: any) =>
      state.isSelected
        ? "!bg-emphasis !text-emphasis"
        : state.isFocused
        ? "!bg-muted !text-emphasis"
        : "!text-default",
  } as const;

  // Put the menu in a portal with a z-index lower than timezone (fixes “see-through”)
  const styles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 45 }),
  };

  return (
    <span className="min-w-32 -mt-[2px] flex h-6 max-w-full items-center justify-start">
      <ReactSelect
        unstyled // <-- THIS removes the default white pill
        instanceId="currency-select"
        classNames={classNames}
        styles={styles}
        menuPortalTarget={typeof window !== "undefined" ? document.body : undefined}
        menuPosition="fixed"
        menuPlacement="bottom"
        value={current}
        onChange={(opt) => {
          if (opt && "value" in opt) setCurrency((opt as any).value.toUpperCase());
        }}
        options={options}
        placeholder="Select…"
        menuIsOpen={menuIsOpen}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        components={{ Menu: CustomMenu, Option: CustomOption }}
      />
    </span>
  );
}
